import { memo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SearchParams } from '@/shared/constants/search-params.constant';
import { useSelectedPolygon } from '@/shared/lib/hooks/use-selected-polygon.hook';
import { buildTableProps } from '@/shared/lib/table';
import { TanstackTable } from '@/shared/ui/table';

import { useDeleteHistogramMutation } from '../../api/histogram-form.api';
import { useSelectedHistogram } from '../../model/hooks/use-selected-histogram.hook';
import { useCreateHistogramFormActions } from '../../model/slice/create-histogram-form.slice';
import { useHistogramsTableSchema } from '../../model/table/histograms.table-schema';
import { HistogramsTableProps } from './histograms-table.types';

const HistogramsTableComponent = ({ items, pages: totalPages, total, tableState, isLoading }: HistogramsTableProps) => {
	const [deleteHistogramMutation] = useDeleteHistogramMutation();
	const { setHistogram, resetHistogram } = useCreateHistogramFormActions();
	const selectedHistogramId = useSelectedHistogram();
	const { setSelectedPolygon } = useSelectedPolygon();
	const [searchParams, setSearchParams] = useSearchParams();

	const onDelete = useCallback(
		(id: string) => {
			deleteHistogramMutation(id).then(() => {
				if (id === selectedHistogramId) {
					resetHistogram();
					searchParams.delete(SearchParams.YachtTrack);
					searchParams.delete(SearchParams.Zoom);
					setSearchParams(searchParams);
				}
			});
		},
		[deleteHistogramMutation, selectedHistogramId, resetHistogram, searchParams, setSearchParams],
	);

	const onView = useCallback(
		(id: string) => {
			if (id === selectedHistogramId) {
				resetHistogram();
				searchParams.delete(SearchParams.YachtTrack);
				searchParams.delete(SearchParams.Zoom);
				setSearchParams(searchParams);
			} else {
				setSelectedPolygon(null);
				setHistogram(id);
				setSearchParams((searchParams) => {
					searchParams.set(SearchParams.YachtTrack, id);
					searchParams.delete(SearchParams.Zoom);
					return searchParams;
				});
			}
		},
		[selectedHistogramId, resetHistogram, searchParams, setSearchParams, setSelectedPolygon, setHistogram],
	);

	const schema = useHistogramsTableSchema({ onView, onDelete, histogramId: selectedHistogramId });
	const tableProps = buildTableProps({
		schema,
		state: tableState,
		data: items,
		total,
		totalPages,
		isLoading,
	});

	return <TanstackTable {...tableProps} />;
};

export default memo(HistogramsTableComponent);
